import {Button} from 'platform/components';
import {
  Box,
  Hide,
  HStack,
  Icon,
  Integer,
  Show,
  Text,
  ThemeColorPath,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {RefObject, useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {TelephonyCall} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {formatCallDuration} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALL_BAR_FLY_DURATION_MS} from '../../../constants/CALL_BAR_FLY_DURATION_MS';
import {CALL_BAR_HANDOVER_MIN_MS} from '../../../constants/CALL_BAR_HANDOVER_MIN_MS';
import {CALL_BAR_HOVER_LEAVE_DELAY_MS} from '../../../constants/CALL_BAR_HOVER_LEAVE_DELAY_MS';
import {CALL_REJECTED_SHOWN_MS} from '../../../constants/CALL_REJECTED_SHOWN_MS';
import {getCallPhoneNumberLabel} from '../../../utils/getCallPhoneNumberLabel';
import {TelephonyCallBarPhase} from '../../../utils/getTelephonyCallBarPhase';
import {AutoHide} from './AutoHide';
import {CallCustomerActions} from './CallCustomerActions';
import {OutboundMobileSteps} from './OutboundMobileSteps';

interface TelephonyCallBarProps extends RequiredTestIdProps {
  call: TelephonyCall;
  /** Mount with a `key` per call and phase: the hand-over timing restarts with each phase. */
  phase: TelephonyCallBarPhase;
  /** The call rings this tab's softphone, so it can be answered or rejected here. */
  isRingingHere: boolean;
  elapsedSeconds: number | Nullish;
  /** Where the connected bar flies to: the top-bar chip. */
  chipRef: RefObject<HTMLElement | null>;
  onAccept: VoidFunction;
  onReject: VoidFunction;
  /** The browser call runs in this tab, so it can be cancelled here. */
  canCancel: boolean;
  onCancel: VoidFunction;
  onOpenCustomer: (customerId: string) => void;
  onCreateCustomer: VoidFunction;
  onFindCustomer: VoidFunction;
  /** The connected bar has landed in the chip. */
  onHandedOver: VoidFunction;
  /** The red rejected bar has been shown long enough. */
  onRejectedShown: VoidFunction;
}

const SEPARATOR = ' · ';
const BAR_WIDTH = 250;
const BAR_BOTTOM_OFFSET = 6;
/** On phones the bar clears the floating assistant button in the bottom-right corner. */
const BAR_BOTTOM_OFFSET_MOBILE = 20;
/** Hints on the right wrap instead of pushing over the caller; narrower on phones. */
const HINT_MAX_WIDTH: ValueByDevice<Integer> = [25, 50];
/** The caller (for an unknown number its only identity) keeps room before anything else shrinks. */
const CALLER_MIN_WIDTH: ValueByDevice<Integer> = [25, 40];
const BAR_SIDE_GUTTER = 8;
/** How small the bar gets when it lands in the chip. */
const FLY_SCALE = 0.12;

// A mobile call turns orange only while it waits for the caller to press 1, like the chip.
const getBackgroundColor = (
  phase: TelephonyCallBarPhase,
  isWaitingForConfirmation: boolean
): ThemeColorPath =>
  match(phase)
    .with('incoming', 'outboundBrowser', () => 'palettes.neutral.900.100' as const)
    .with('outboundMobile', () =>
      isWaitingForConfirmation
        ? ('palettes.orange.90.100' as const)
        : ('palettes.neutral.900.100' as const)
    )
    .with('connected', () => 'palettes.green.80.100' as const)
    .with('rejected', () => 'palettes.red.80.100' as const)
    .exhaustive();

/**
 * The bottom-centre bar for a call that still needs the viewer: ringing in, ringing out, or the
 * press-1 steps of a mobile call. Once someone picks up it turns green without buttons, waits at
 * least two seconds (and as long as the cursor rests on it), then shrinks into the top-bar chip,
 * which from then on is the only call UI.
 */
export function TelephonyCallBar(props: TelephonyCallBarProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [isMinShownElapsed, setMinShownElapsed] = useState(false);
  const [isHovered, setHovered] = useState(false);
  const [leaveCount, setLeaveCount] = useState(0);
  const [flyTransform, setFlyTransform] = useState<string | null>(null);

  const isConnected = props.phase === 'connected';
  const isReadyToFly = isConnected && isMinShownElapsed && !isHovered && isNil(flyTransform);

  const phoneNumber = getCallPhoneNumberLabel(props.call);
  const customer = props.call.customerName ?? phoneNumber;
  const outboundStatus = props.call.direction === 'outbound' ? props.call.status : null;
  const lineLabel =
    props.call.direction === 'outbound'
      ? i18n.t('page.telephony.indicator.fromLine')
      : i18n.t('page.telephony.indicator.toLine');
  const line = [props.call.line.name, formatPhoneNumber(props.call.line.phoneNumber)].join(
    SEPARATOR
  );

  const title = match(props.phase)
    .with('incoming', () => i18n.t('page.telephony.indicator.incomingCall'))
    .with('outboundBrowser', () => i18n.t('page.telephony.indicator.stepBrowserStarted'))
    .with('outboundMobile', () => i18n.t('page.telephony.bar.callingViaMobile'))
    .with('connected', () =>
      [i18n.t('page.telephony.bar.connected'), formatCallDuration(props.elapsedSeconds ?? 0)].join(
        SEPARATOR
      )
    )
    .with('rejected', () => i18n.t('page.telephony.bar.rejected'))
    .exhaustive();

  const handleFly = () => {
    const frame = frameRef.current;
    const chip = props.chipRef.current;

    if (isNil(frame) || isNil(chip)) {
      props.onHandedOver();
      return;
    }

    const from = frame.getBoundingClientRect();
    const to = chip.getBoundingClientRect();
    const deltaX = to.left + to.width / 2 - (from.left + from.width / 2);
    const deltaY = to.top + to.height / 2 - (from.top + from.height / 2);

    setFlyTransform(`translate(${deltaX}px, ${deltaY}px) scale(${FLY_SCALE})`);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    setLeaveCount((count) => count + 1);
  };

  return (
    <BarFrame
      ref={frameRef}
      role="region"
      aria-label={i18n.t('page.telephony.bar.ariaLabel')}
      $flyTransform={flyTransform}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
      data-testid={suffixTestId('bar', props)}
    >
      <Show when={isConnected}>
        <AutoHide delayMs={CALL_BAR_HANDOVER_MIN_MS} onElapsed={() => setMinShownElapsed(true)} />
      </Show>
      <Show when={isReadyToFly}>
        <AutoHide
          key={leaveCount}
          delayMs={leaveCount > 0 ? CALL_BAR_HOVER_LEAVE_DELAY_MS : 0}
          onElapsed={handleFly}
        />
      </Show>
      <Show when={isNotNil(flyTransform)}>
        <AutoHide delayMs={CALL_BAR_FLY_DURATION_MS} onElapsed={props.onHandedOver} />
      </Show>
      <Show when={props.phase === 'rejected'}>
        <AutoHide delayMs={CALL_REJECTED_SHOWN_MS} onElapsed={props.onRejectedShown} />
      </Show>

      <Box
        backgroundColor={getBackgroundColor(
          props.phase,
          outboundStatus === 'waiting_for_confirmation'
        )}
        borderRadius="medium"
        boxShadow="elevation_2"
        paddingHorizontal={4}
        paddingVertical={3}
      >
        <HStack spacing={4} align="center">
          <Icon value="communication/call" size={6} color="text.white" />
          <VStack spacing={1} grow={1} minWidth={CALLER_MIN_WIDTH}>
            <Text size="xSmall" color="white" data-testid={suffixTestId('bar-title', props)}>
              {title}
            </Text>
            <HStack spacing={2} align="baseline">
              <Text
                color="white"
                alternative
                noWrap
                data-testid={suffixTestId('bar-customer', props)}
              >
                {customer}
              </Text>
              <Show when={isNotNil(props.call.customerName) && props.phase !== 'outboundMobile'}>
                <Text size="small" color="white" noWrap>
                  {phoneNumber}
                </Text>
              </Show>
            </HStack>
          </VStack>

          <Hide onMobile onTablet>
            {match(props.phase)
              .with('outboundMobile', () => (
                <Show when={outboundStatus}>
                  {(status) => (
                    <OutboundMobileSteps
                      status={status}
                      customer={customer}
                      direction="row"
                      isInverted
                      data-testid={suffixTestId('bar', props)}
                    />
                  )}
                </Show>
              ))
              .with('connected', () =>
                props.call.recordingNoticeMode === 'operator' ? (
                  <Text
                    size="small"
                    color="white"
                    alternative
                    data-testid={suffixTestId('bar-recordingNotice', props)}
                  >
                    {i18n.t('page.telephony.recording.operatorBanner')}
                  </Text>
                ) : (
                  <Text size="small" color="white">
                    {i18n.t('page.telephony.bar.controlsMoved')}
                  </Text>
                )
              )
              .with('rejected', () => null)
              .with('incoming', 'outboundBrowser', () => (
                <HStack spacing={4} align="center">
                  <VStack spacing={1}>
                    <Text size="xSmall" color="white">
                      {lineLabel}
                    </Text>
                    <Text size="small" color="white" noWrap>
                      {line}
                    </Text>
                  </VStack>
                  <CallCustomerActions
                    call={props.call}
                    isOnDarkSurface
                    onOpenCustomer={props.onOpenCustomer}
                    onCreateCustomer={props.onCreateCustomer}
                    onFindCustomer={props.onFindCustomer}
                    data-testid={suffixTestId('bar', props)}
                  />
                </HStack>
              ))
              .exhaustive()}
          </Hide>

          {match(props.phase)
            .with('incoming', () =>
              props.isRingingHere ? (
                <HStack spacing={2}>
                  <Button
                    variant="danger"
                    title={i18n.t('general.actions.reject')}
                    onClick={props.onReject}
                    data-testid={suffixTestId('bar-reject', props)}
                  />
                  <Button
                    variant="success"
                    leftIcon="communication/call"
                    title={i18n.t('general.actions.accept')}
                    onClick={props.onAccept}
                    data-testid={suffixTestId('bar-accept', props)}
                  />
                </HStack>
              ) : (
                <Box flexShrink={1} maxWidth={HINT_MAX_WIDTH}>
                  <Text size="small" color="white">
                    {i18n.t('page.telephony.bar.answerOnMobile')}
                  </Text>
                </Box>
              )
            )
            .with('outboundBrowser', () => (
              <Show when={props.canCancel}>
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.cancel')}
                  onClick={props.onCancel}
                  data-testid={suffixTestId('bar-cancel', props)}
                />
              </Show>
            ))
            .with('outboundMobile', () => (
              <Box flexShrink={1} maxWidth={HINT_MAX_WIDTH}>
                <Text size="small" color="white">
                  {i18n.t('page.telephony.bar.endOnMobile')}
                </Text>
              </Box>
            ))
            .with('connected', 'rejected', () => null)
            .exhaustive()}
        </HStack>
      </Box>
    </BarFrame>
  );
}

const BarFrame = styled.div<{$flyTransform: string | null}>`
  position: fixed;
  left: 50%;
  bottom: ${({theme}) => theme.getSize(BAR_BOTTOM_OFFSET)};
  width: min(
    ${({theme}) => theme.getSize(BAR_WIDTH)},
    calc(100vw - ${({theme}) => theme.getSize(BAR_SIDE_GUTTER)})
  );
  z-index: ${({theme}) => theme.zIndices.HEADER_POPPER};
  transform: translateX(-50%) ${({$flyTransform}) => $flyTransform ?? ''};
  transition: transform ${CALL_BAR_FLY_DURATION_MS}ms ease-in;

  @media ${({theme}) => theme.devices.mobile} {
    bottom: ${({theme}) => theme.getSize(BAR_BOTTOM_OFFSET_MOBILE)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
